import BlogIcon from "@/assets/colorable/blog.svg?react";
import BuildingIcon from "@/assets/colorable/building.svg?react";
import BuildingFilledIcon from "@/assets/colorable/building_filled.svg?react";
import CampaignIcon from "@/assets/colorable/campaign.svg?react";
import CampaignFeedIcon from "@/assets/colorable/campaign_feed.svg?react";
import CampaignFeedFilledIcon from "@/assets/colorable/campaign_feed_filled.svg?react";
import CampaignFilledIcon from "@/assets/colorable/campaign_filled.svg?react";
import CampaignPlusIcon from "@/assets/colorable/campaign_plus.svg?react";
import ChartsIcon from "@/assets/colorable/charts.svg?react";
import CommentIcon from "@/assets/colorable/comment.svg?react";
import EyeIcon from "@/assets/colorable/eye.svg?react";
import EyeFilledIcon from "@/assets/colorable/eye_filled.svg?react";
import GroupIcon from "@/assets/colorable/group.svg?react";
import HandshakeIcon from "@/assets/colorable/handshake.svg?react";
import HelpIcon from "@/assets/colorable/help.svg?react";
import InfluencersIcon from "@/assets/colorable/influencers.svg?react";
import InfluencersFilledIcon from "@/assets/colorable/influencers_filled.svg?react";
import LinkIcon from "@/assets/colorable/link.svg?react";
import MessagesIcon from "@/assets/colorable/messages.svg?react";
import MessagesFilledIcon from "@/assets/colorable/messages_filled.svg?react";
import SourceContentIcon from "@/assets/colorable/source_content.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import RefluencedLogo from "@/assets/images/refluenced.svg";
import NavbarAgencyProfile from "@/components/NavBar/NavbarAgencyProfile";
import NavbarCompanyProfile from "@/components/NavBar/NavbarCompanyProfile";
import {useHelpUrl} from "@/hooks/useNavigationHooks";
import {
  useAgencyId,
  useIntercomDisabled,
  useIsAgency,
  useIsCompany,
  useIsInfuencer,
  useIsSubbrand,
  useSelectedCompanyReactive,
} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {
  brandImpersonationAtom,
  createCampaignForCompanyAtom,
  isFeedbackModalOpenAtom,
  isMainSidebarCollapsedAtom,
  isMobileSidebarOpenAtom,
} from "@/store/jotaiAtoms";
import useNotificationStore from "@/store/notification";
import {Link, useLocation} from "@tanstack/react-router";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {PanelLeftClose, PanelLeftOpen} from "lucide-react";
import {useEffect, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useMedia} from "react-use";
import {useIntercom} from "react-use-intercom";

const Sidebar: FC = () => {
  const {t} = useTranslation(["navigation", "common"]);
  const location = useLocation();
  const selectedCompany = useSelectedCompanyReactive();
  const isAgency = useIsAgency();
  const isCompany = useIsCompany();
  const isSubbrand = useIsSubbrand();
  const isInfluencer = useIsInfuencer();
  const isBrandUser = isCompany || isSubbrand || isAgency;
  const isInfluencerUser = isInfluencer && !isBrandUser;
  const agencyId = useAgencyId();
  const setCreateCampaignForCompany = useSetAtom(createCampaignForCompanyAtom);
  const openFeedbackModal = useSetAtom(isFeedbackModalOpenAtom);
  const [isCollapsed, setIsCollapsed] = useAtom(isMainSidebarCollapsedAtom);
  const [isMobileOpen, setIsMobileOpen] = useAtom(isMobileSidebarOpenAtom);
  const isImpersonatingBrand = !!useAtomValue(brandImpersonationAtom);
  const displayCollapsed = isMobileOpen ? false : isCollapsed;

  const isMdUp = useMedia("(min-width: 768px)");
  useEffect(() => {
    setIsMobileOpen(false);
  }, [isMdUp, location.pathname, setIsMobileOpen]);
  const {showMessages: showIntercom} = useIntercom();
  const intercomDisabled = useIntercomDisabled();
  const helpUrl = useHelpUrl();

  const hasChatsNeedingReply = useNotificationStore(state => !!state.notificationCounts.chat.needs_reply);

  const isActive = (path: string) => location.pathname.includes(path);
  const isCampaignsActive = isInfluencerUser ? location.pathname === "/campaigns" : isActive("/dashboard");
  const isInfluencersActive = isActive("/influencers");
  const isGroupsActive = isActive("/groups") || isActive("/group/");
  const isMediaLibraryActive = isActive("/media-library");
  const isSearchVisibilityActive = isActive("/search-visibility");
  const isCampaignFeedActive = !isInfluencerUser && location.pathname === "/campaigns";
  const isInboxActive = isActive("/chat");
  const isCustomersActive = isActive("/admins/customers");
  const isCustomerSuccessActive = isActive("/admins/customer-success");
  const isAdminHubActive = isActive("/admins/hub");
  const isBoostEstimateActive = isActive("/admins/boost-estimate");
  const isAdminOverviewActive = location.pathname === "/admins" || location.pathname === "/admins/";

  return (
    <>
      <div
        onClick={() => setIsMobileOpen(false)}
        aria-hidden
        className={clsxm(
          "bg-neutral-black/40 fixed inset-0 z-1150 transition-opacity duration-300 ease-out md:hidden",
          isImpersonatingBrand && "top-14",
          isMobileOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        className={clsxm(
          "bg-sidebar text-sidebar-foreground fixed inset-y-0 left-0 z-1200 flex w-50 flex-col shadow-md transition duration-300 ease-out max-md:pb-(--cookie-banner-height) md:translate-x-0 md:transition-[width,translate]",
          isImpersonatingBrand && "top-14",
          displayCollapsed ? "md:w-14" : "md:w-50",
          isMobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full",
        )}>
        <div
          className={clsxm(
            "flex shrink-0 items-center pt-5 pb-4",
            displayCollapsed ? "justify-center px-2" : "justify-between px-5",
          )}>
          {!displayCollapsed && (
            <Link to="/">
              <img src={RefluencedLogo} className="size-7" alt="Refluenced" />
            </Link>
          )}
          <button
            onClick={() => (isMobileOpen ? setIsMobileOpen(false) : setIsCollapsed(!displayCollapsed))}
            className="text-muted-foreground hover:text-foreground transition-colors">
            {displayCollapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-2 whitespace-nowrap">
          <div className="group relative">
            <Link
              to={
                isInfluencerUser
                  ? "/campaigns"
                  : isAgency
                    ? selectedCompany
                      ? "/agency/$agencyId/subbrand/$companyId/dashboard"
                      : "/agency/$agencyId/dashboard"
                    : "/company/$companyId/dashboard"
              }
              params={{
                agencyId: agencyId!,
                companyId: selectedCompany?.id!,
              }}
              className="no-underline"
              title={displayCollapsed ? (t("my_campaigns") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isCampaignsActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                {isCampaignsActive ? (
                  <CampaignFilledIcon className="size-4 shrink-0" />
                ) : (
                  <CampaignIcon className="size-4 shrink-0" />
                )}
                {!displayCollapsed && t("my_campaigns")}
              </div>
            </Link>
            {!displayCollapsed && !isInfluencerUser && (
              <Can I="create" a="Campaign">
                <button
                  onClick={() => selectedCompany && setCreateCampaignForCompany(selectedCompany.id)}
                  className="bg-sidebar-accent text-primary-dark-solid absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md opacity-0 transition-opacity group-hover:opacity-100">
                  <CampaignPlusIcon className="size-3.5" />
                </button>
              </Can>
            )}
          </div>

          <Link
            to="/influencers-list"
            className="no-underline"
            title={displayCollapsed ? (t("influencers") as string) : undefined}>
            <div
              className={clsxm(
                "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                displayCollapsed ? "justify-center" : "gap-2.5",
                isInfluencersActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
              )}>
              {isInfluencersActive ? (
                <InfluencersFilledIcon className="size-4 shrink-0" />
              ) : (
                <InfluencersIcon className="size-4 shrink-0" />
              )}
              {!displayCollapsed && t("influencers")}
            </div>
          </Link>

          {!isInfluencerUser && (!!selectedCompany || (isAgency && !!agencyId)) && (
            <Link
              to={
                isAgency
                  ? selectedCompany
                    ? "/agency/$agencyId/subbrand/$companyId/groups"
                    : "/agency/$agencyId/groups"
                  : "/company/$companyId/groups"
              }
              params={{
                agencyId: agencyId!,
                companyId: selectedCompany?.id!,
              }}
              className="no-underline"
              title={displayCollapsed ? (t("groups") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isGroupsActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                <GroupIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("groups")}
              </div>
            </Link>
          )}
          {!isInfluencerUser && (!!selectedCompany || (isAgency && !!agencyId)) && (
            <Link
              to={
                isAgency
                  ? selectedCompany
                    ? "/agency/$agencyId/subbrand/$companyId/media-library"
                    : "/agency/$agencyId/media-library"
                  : "/company/$companyId/media-library"
              }
              params={{
                agencyId: agencyId!,
                companyId: selectedCompany?.id!,
              }}
              className="no-underline"
              title={displayCollapsed ? (t("media_library") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isMediaLibraryActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                <SourceContentIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("media_library")}
              </div>
            </Link>
          )}

          {!!selectedCompany && (
            <Link
              to={
                isAgency
                  ? "/agency/$agencyId/subbrand/$companyId/search-visibility"
                  : "/company/$companyId/search-visibility"
              }
              params={{
                agencyId: agencyId!,
                companyId: selectedCompany.id!,
              }}
              search={{tab: "overview"}}
              className="no-underline"
              title={displayCollapsed ? (t("search_visibility") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isSearchVisibilityActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                {isSearchVisibilityActive ? (
                  <EyeFilledIcon className="size-4 shrink-0" />
                ) : (
                  <EyeIcon className="size-4 shrink-0" />
                )}
                {!displayCollapsed && t("search_visibility")}
              </div>
            </Link>
          )}

          {!!selectedCompany && (
            <Link
              to={
                isAgency
                  ? "/agency/$agencyId/chat/$companyId/{-$campaignId}/{-$influencerUserId}"
                  : "/company/$companyId/chat/{-$campaignId}/{-$influencerUserId}"
              }
              params={{
                agencyId: agencyId!,
                campaignId: undefined,
                companyId: selectedCompany.id!,
                influencerUserId: undefined,
              }}
              className="no-underline"
              title={displayCollapsed ? (t("messages") as string) : undefined}>
              <div
                className={clsxm(
                  "relative mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isInboxActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                {isInboxActive ? (
                  <MessagesFilledIcon className="size-4 shrink-0" />
                ) : (
                  <MessagesIcon className="size-4 shrink-0" />
                )}
                {!displayCollapsed && (
                  <>
                    <span className="flex-1">{t("messages")}</span>
                    {hasChatsNeedingReply && <div className="bg-danger-raspberry size-2 shrink-0 rounded-full" />}
                  </>
                )}
                {displayCollapsed && hasChatsNeedingReply && (
                  <div className="bg-danger-raspberry absolute top-1.5 right-2.5 size-2 rounded-full border border-white" />
                )}
              </div>
            </Link>
          )}

          <div className="border-neutral-grey-light-dark mx-3 my-3 border-t" />

          {!displayCollapsed && (
            <div className="mb-1">
              <span className="text-muted-foreground text-2xs px-3 font-medium tracking-wider uppercase">
                {t("community")}
              </span>
            </div>
          )}

          {!isInfluencerUser && (
            <Link
              to="/campaigns"
              className="no-underline"
              title={displayCollapsed ? (t("campaign_feed") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isCampaignFeedActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                {isCampaignFeedActive ? (
                  <CampaignFeedFilledIcon className="size-4 shrink-0" />
                ) : (
                  <CampaignFeedIcon className="size-4 shrink-0" />
                )}
                {!displayCollapsed && t("campaign_feed")}
              </div>
            </Link>
          )}

          <a
            href={`${import.meta.env.VITE_APP_API_BASE_URL ?? ""}/blog`}
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline"
            title={displayCollapsed ? (t("blog") as string) : undefined}>
            <div
              className={clsxm(
                "text-foreground hover:bg-muted mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                displayCollapsed ? "justify-center" : "gap-2.5",
              )}>
              <BlogIcon className="size-4 shrink-0" />
              {!displayCollapsed && t("blog")}
            </div>
          </a>

          {intercomDisabled ? (
            <a
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline"
              title={displayCollapsed ? (t("help") as string) : undefined}>
              <div
                className={clsxm(
                  "text-foreground hover:bg-muted mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                )}>
                <HelpIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("help")}
              </div>
            </a>
          ) : (
            <button
              onClick={() => showIntercom()}
              className="w-full text-left"
              title={displayCollapsed ? (t("help") as string) : undefined}>
              <div
                className={clsxm(
                  "text-foreground hover:bg-muted mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                )}>
                <HelpIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("help")}
              </div>
            </button>
          )}

          <Can I="read" a="AdminMenu">
            <div className="border-neutral-grey-light-dark mx-3 my-3 border-t" />

            {!displayCollapsed && (
              <div className="mb-1">
                <span className="text-muted-foreground text-2xs px-3 font-medium tracking-wider uppercase">
                  {t("admin")}
                </span>
              </div>
            )}

            <Link
              to="/admins"
              className="no-underline"
              title={displayCollapsed ? (t("admin_overview") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isAdminOverviewActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                <LinkIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("admin_overview")}
              </div>
            </Link>

            <Link
              to="/admins/hub"
              className="no-underline"
              title={displayCollapsed ? (t("admin_hub") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isAdminHubActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                <WarningIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("admin_hub")}
              </div>
            </Link>

            <Link
              to="/admins/customers"
              className="no-underline"
              title={displayCollapsed ? (t("customers") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isCustomersActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                {isCustomersActive ? (
                  <BuildingFilledIcon className="size-4 shrink-0" />
                ) : (
                  <BuildingIcon className="size-4 shrink-0" />
                )}
                {!displayCollapsed && t("customers")}
              </div>
            </Link>

            <Link
              to="/admins/customer-success"
              className="no-underline"
              title={displayCollapsed ? (t("customer_success") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isCustomerSuccessActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                <HandshakeIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("customer_success")}
              </div>
            </Link>

            <Link
              to="/admins/boost-estimate"
              className="no-underline"
              title={displayCollapsed ? (t("boost_estimate") as string) : undefined}>
              <div
                className={clsxm(
                  "mb-0.5 flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  displayCollapsed ? "justify-center" : "gap-2.5",
                  isBoostEstimateActive ? "bg-primary-sky/40 text-foreground" : "text-foreground hover:bg-muted",
                )}>
                <ChartsIcon className="size-4 shrink-0" />
                {!displayCollapsed && t("boost_estimate")}
              </div>
            </Link>
          </Can>
        </nav>

        <div className="shrink-0 px-2 pb-2">
          <button
            onClick={() => openFeedbackModal(true)}
            className="w-full text-left"
            title={displayCollapsed ? (t("give_feedback") as string) : undefined}>
            <div
              className={clsxm(
                "text-foreground hover:bg-muted flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                displayCollapsed ? "justify-center" : "gap-2.5",
              )}>
              <CommentIcon className="size-4 shrink-0" />
              {!displayCollapsed && t("give_feedback")}
            </div>
          </button>
        </div>

        <div className="border-neutral-grey-light-dark shrink-0 border-t">
          {isAgency ? (
            <NavbarAgencyProfile isCollapsed={displayCollapsed} />
          ) : (
            <NavbarCompanyProfile isCollapsed={displayCollapsed} />
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
